/** @jsxImportSource @emotion/react */

import { useQuery } from '@apollo/client'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import IconButton from '@mui/material/IconButton'
import { Link, useSearchParams } from 'react-router-dom'
import Divider from '@mui/material/Divider'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { Loader } from 'components/Loader'
import { PlatformSlug, TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import * as tableButtonsClasses from 'components/TableButtons/styles'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { FIND_ALL_STUDY_GUIDE_SECTIONS } from 'queries/studyGuide'
import {
  findAllStudyGuideSection,
  findAllStudyGuideSectionVariables,
} from '__generatedTypes__/findAllStudyGuideSection'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { FIND_STUDY_SCHEDULE } from 'queries/studySchedule'
import { findStudySchedule, findStudyScheduleVariables } from '__generatedTypes__/findStudySchedule'
import FAQ, { FAQ_TAB } from 'pagesNew/FAQ'
import { FaqPages } from '__generatedTypes__/globalTypes'
import TabsNavigation from 'components/TabsNavigation'
import { TabsItem } from 'types/tabsItem'
import { isStudyScheduleAvailable } from 'pagesNew/StudySchedule/platformConfig'
import { AllStudyGuidesContent } from '../AllStudyGuides'
import { PAGE_QUERY_PARAM } from '../BlogPostList'
import * as commonClasses from '../SuccessStories/styles'
import { SectionItem } from '../SuccessStories/SectionItem'
import { StudyGuideSchedule } from '../StudyGuideSchedule'
import { STUDY_GUIDE_SECTIONS_TAB, STUDY_GUIDES_SUB_TAB_QUERY_PARAM, STUDY_GUIDES_SUB_TABS } from '../consts'
import * as blogClasses from '../styles'
import * as classes from './styles'

type Props = {
  platform: Platform
}

const StudyGuideSections = ({ platform }: Props) => {
  const { getLink } = useLink()

  // We show Recommended Study Schedule in Settings modal on Study Schedule page
  // or as first tab in Study Guides study tool for Casper only
  const hasRecommendedStudySchedule =
    isStudyScheduleAvailable(platform.slug as PlatformSlug) || platform.slug === PlatformSlug.CasperBooster

  const { data, loading } = useQuery<findAllStudyGuideSection, findAllStudyGuideSectionVariables>(
    FIND_ALL_STUDY_GUIDE_SECTIONS,
    {
      fetchPolicy: 'network-only',
      variables: {
        query: {
          take: TAKE_ALL_ITEMS_COUNT,
          where: {
            platform: {
              id: platform.id,
            },
          },
        },
      },
    }
  )

  const { data: studySchedule, loading: loadingStudySchedule } = useQuery<
    findStudySchedule,
    findStudyScheduleVariables
  >(FIND_STUDY_SCHEDULE, {
    variables: {
      platformId: platform.id,
    },
  })

  const successSections = data?.findAllStudyGuideSection?.items || []

  if (loading || loadingStudySchedule) {
    return <Loader />
  }

  return (
    <div>
      {successSections.length ? (
        <ul css={commonClasses.list}>
          {hasRecommendedStudySchedule && <StudyGuideSchedule studySchedule={studySchedule?.findStudySchedule} />}
          {successSections.map((section) => {
            const sectionItems = section.studyGuide.map(({ id, title }) => ({ id, label: title }))

            return (
              <li key={section.id} css={commonClasses.listItem}>
                <div>
                  <Typography variant="subtitle2" css={commonClasses.title} data-testid={`section-${section.id}`}>
                    {section.title}
                  </Typography>
                  <SectionItem orderIds={section.studyGuideIds} items={sectionItems} />
                </div>
                <Link to={getLink(cmsRoutes.ORGANIZE_EDIT_STUDY_GUIDES, { id: section.id })}>
                  <IconButton css={commonClasses.editButton} LinkComponent={Link} data-testid="edit-button">
                    <EditIcon />
                  </IconButton>
                </Link>
              </li>
            )
          })}
        </ul>
      ) : (
        <>
          {hasRecommendedStudySchedule && <StudyGuideSchedule studySchedule={studySchedule?.findStudySchedule} />}
          <Typography variant="h6" css={commonClasses.noSectionsTitle}>
            No sections found
          </Typography>
        </>
      )}

      <Link css={commonClasses.addLink} to={getLink(cmsRoutes.ORGANIZE_CREATE_STUDY_GUIDES)}>
        <Button css={commonClasses.addButton} variant="outlined" color="blue" size="large" fullWidth>
          Add new section in Study Guides <AddRoundedIcon css={tableButtonsClasses.iconPlus} />
        </Button>
      </Link>

      <Divider css={classes.divider} />

      <div>
        <TabsNavigation tabs={[FAQ_TAB]} value={FAQ_TAB} css={classes.tabs} />

        <FAQ page={FaqPages.StudyGuide} />
      </div>
    </div>
  )
}

export const StudyGuideContent = ({ platform }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeSubTab =
    STUDY_GUIDES_SUB_TABS.find(({ id }) => id === searchParams.get(STUDY_GUIDES_SUB_TAB_QUERY_PARAM)) ??
    STUDY_GUIDE_SECTIONS_TAB

  const handleChangeSubTab = (tab: TabsItem) => {
    setSearchParams(
      (currentParams) => {
        const nextParams = new URLSearchParams(currentParams)

        tab.id === STUDY_GUIDE_SECTIONS_TAB.id
          ? nextParams.delete(STUDY_GUIDES_SUB_TAB_QUERY_PARAM)
          : nextParams.set(STUDY_GUIDES_SUB_TAB_QUERY_PARAM, tab.id)

        nextParams.delete(PAGE_QUERY_PARAM)

        return nextParams
      },
      { replace: true }
    )
  }

  return (
    <div>
      <TabsNavigation
        css={blogClasses.subTabs}
        tabs={STUDY_GUIDES_SUB_TABS}
        value={activeSubTab}
        onChange={handleChangeSubTab}
      />

      {activeSubTab.id === STUDY_GUIDE_SECTIONS_TAB.id ? (
        <StudyGuideSections platform={platform} />
      ) : (
        <AllStudyGuidesContent platform={platform} />
      )}
    </div>
  )
}

export const StudyGuide = withPlatformCheck(StudyGuideContent)
